export interface Verdict {
  text: string;
  colour: string;
}

// A certificate's window said as a badge: whether it is inside it, and how far from the edge. The countdown is the
// whole reason anybody pastes one in a hurry, so it is the headline rather than a note under a date.
export function validity(notBefore: Date | null, notAfter: Date | null, now: number): Verdict | null {
  if (notAfter === null) return null;
  if (notBefore !== null && now < notBefore.getTime()) {
    return { text: `Valid ${relative(notBefore.getTime(), now)}`, colour: "yellow" };
  }
  if (now > notAfter.getTime()) return { text: `Expired ${relative(notAfter.getTime(), now)}`, colour: "red" };
  const left = notAfter.getTime() - now;
  return { text: `Expires ${relative(notAfter.getTime(), now)}`, colour: left < SOON ? "yellow" : "teal" };
}

// Thirty days is the window a renewal is supposed to happen in, so it is also the one worth a colour of its own.
const SOON = 30 * 86400_000;

const NEAR: [Intl.RelativeTimeFormatUnit, number][] = [["day", 86400], ["hour", 3600], ["minute", 60], ["second", 1]];
// A plain 365 days rather than the Gregorian average, so a certificate issued for a year reads as one and not as
// eleven months; the month below it is the average, there being no such thing as a plain one.
const FAR: [Intl.RelativeTimeFormatUnit, number][] = [["year", 31536000], ["month", 2629746]];

// Days for anything inside a quarter, because a certificate is renewed on a count of days and "in 3 months" is not a
// date anybody can act on. Past that the count stops being useful and the calendar words take over.
const QUARTER = 90 * 86400;

// Counted rather than named: a certificate that expired yesterday and one that expires today are not the same news,
// and "yesterday" is a claim about the calendar where this is a claim about how long is left.
const WORDS = new Intl.RelativeTimeFormat(undefined, { numeric: "always" });

export function relative(target: number, now: number): string {
  const seconds = Math.round((target - now) / 1000);
  const size = Math.abs(seconds);
  const units = size < QUARTER ? NEAR : FAR;
  const unit = units.find(([, span]) => size >= span) ?? units[units.length - 1];
  return WORDS.format(Math.trunc(seconds / unit[1]), unit[0]);
}
